import React, { useState } from 'react';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { Folder as FolderIcon, Edit2, Trash2, Check, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { FolderResponse } from '../../services/folderApi';
import { Skeleton } from '../../../../components/ui/skeleton';

interface FolderListProps {
  folders: FolderResponse[];
  activeFolderId: number | null;
  onFolderSelect: (folderId: number) => void;
  onUpdateFolder: (folderId: number, newName: string) => void;
  onDeleteFolder: (folderId: number) => void;
  isLoading: boolean;
}

export const FolderList: React.FC<FolderListProps> = ({
  folders,
  activeFolderId,
  onFolderSelect,
  onUpdateFolder,
  onDeleteFolder,
  isLoading,
}) => {
  const [editingFolder, setEditingFolder] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<number | null>(null);

  const handleEdit = (folder: FolderResponse, e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingFolder?.id === folder.id) {
      setEditingFolder(null);
    } else {
      setEditingFolder({ id: folder.id, name: folder.folder_name });
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingFolder) {
      onUpdateFolder(editingFolder.id, editingFolder.name);
      setEditingFolder(null);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFolder(null);
  };

  const handleDelete = (folderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteFolder(folderId);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (folders.length === 0) {
    return (
      <div className="flex flex-col text-text-primary items-center justify-center h-40 text-center p-4">
        <FolderIcon size={24} className=" mb-2" />
        <p className="text-sm ">No folders yet</p>
        <p className="text-xs ">Create a new folder to get started</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px]">
      <div className=" text-text-primary space-y-2 p-2">
        {folders.map((folder) => (
          <div
            key={folder.id}
            onClick={() => onFolderSelect(folder.id)}
            className={`group flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
              folder.id === activeFolderId
                ? 'bg-background-card text-primary'
                : 'hover:bg-background '
            }`}
          >
            <div className="flex items-center gap-2 flex-1">
              <FolderIcon size={16} />
              {editingFolder?.id === folder.id ? (
                <form onSubmit={handleUpdate} className="flex-1">
                  <Input
                    value={editingFolder.name}
                    onChange={(e) =>
                      setEditingFolder({
                        ...editingFolder,
                        name: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="h-6 py-1"
                  />
                </form>
              ) : (
                <span className="truncate">{folder.folder_name}</span>
              )}
            </div>
            <div className="flex gap-1 invisible group-hover:visible">
              {editingFolder?.id === folder.id ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleUpdate}
                  >
                    <Check size={16} className="text-green-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleCancelEdit}
                  >
                    <X size={16} className="text-red-500" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => handleEdit(folder, e)}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => handleDelete(folder.id, e)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog
        open={deleteFolder !== null}
        onOpenChange={() => setDeleteFolder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              folder and all its contents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteFolder) {
                  onDeleteFolder(deleteFolder);
                  setDeleteFolder(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScrollArea>
  );
};
